'use client';

import { motion, useReducedMotion } from 'motion/react';

import { cn } from '@/lib/utils';

export type MiniBar = {
  label: string;
  value: string;
  /** 0–100, relative to the pair's own max. */
  percent: number;
  tone: 'before' | 'after';
};

/**
 * Paired horizontal bars used inside leak accordions: before (hatched, full
 * width) vs after (solid, shrunk). Bars animate in whenever `replayKey`
 * changes — the parent bumps it on each open so the comparison replays.
 */
export function BeforeAfterMinibars({
  bars,
  replayKey,
  className,
}: {
  bars: MiniBar[];
  replayKey: number;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <div
      className={cn(
        'flex flex-col gap-3 border border-edge bg-muted/30 p-4',
        className
      )}
    >
      {bars.map((bar) => (
        <div key={`${bar.tone}-${bar.label}`}>
          <div className="mb-1.5 flex items-baseline justify-between gap-4">
            <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
              {bar.label}
            </span>
            <span
              className={cn(
                'font-mono text-xs font-medium tabular-nums',
                bar.tone === 'before'
                  ? 'text-muted-foreground'
                  : 'text-foreground'
              )}
            >
              {bar.value}
            </span>
          </div>

          <div className="h-3 overflow-hidden bg-muted">
            <motion.div
              key={replayKey}
              initial={reduceMotion ? false : { width: 0 }}
              animate={{ width: `${bar.percent}%` }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={cn(
                'h-full',
                bar.tone === 'before'
                  ? 'bg-[repeating-linear-gradient(45deg,var(--color-muted-foreground),var(--color-muted-foreground)_4px,transparent_4px,transparent_8px)]'
                  : 'bg-foreground'
              )}
              style={{ width: `${bar.percent}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
